import React, { useState } from "react"
import { useSelector } from "react-redux"
import { Modal } from "react-bootstrap"
import ReactApexChart from "react-apexcharts"
import apexRu from "apexcharts/dist/locales/ru.json"

import { httpOk, GET } from "helpers/api"
import { formatMoney } from "helpers/helpers"
import AnalyticsFilterBar, { FilterField, FilterSelect, FilterSegment } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildAnalyticsUrl, localDateString } from "../parts/analyticsDates"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "12px",
	boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
}

const mutedStyle = {
	color: "var(--dark-text-muted, #8791a1)",
	fontSize: "12px",
}

const inputStyle = { borderRadius: "10px", minHeight: "38px" }

const headerThStyle = { border: "none", boxShadow: "none", background: "var(--dark-surface, #fff)", fontSize: "13px" }

const SALE_COLOR = "#4c6ef0"
const COST_COLOR = "#f59f00"

// Цена держится, пока её не поменяют, — длинные периоды до сегодняшнего дня включительно,
// чтобы «стало» совпадало с тем, по чему касса продаёт сейчас
const longRangeOptions = [
	{ id: 90, label: "3 месяца" },
	{ id: 180, label: "6 месяцев" },
	{ id: 365, label: "12 месяцев" },
]

const metricTabs = [
	{ id: "sale", label: "Цена продажи", icon: "uil-tag-alt" },
	{ id: "cost", label: "Себестоимость", icon: "uil-coins" },
]

const productTypeOptions = [
	{ id: 1, name: "Товары и блюда" },
	{ id: 2, name: "Заготовки" },
	{ id: 0, name: "Сырьё" },
	{ id: 3, name: "Модификаторы" },
	{ id: 4, name: "Услуги" },
]

const directionOptions = [
	{ id: "all", name: "Все позиции" },
	{ id: "increase", name: "Только рост" },
	{ id: "decrease", name: "Только снижение" },
	{ id: "unchanged", name: "Без изменений" },
]

// Источник смены (entity_type истории цен) и просмотр документа — как в окне «История цен»
const SOURCE_NAMES = {
	products: "Карточка товара",
	documents_in: "Приход",
	document_preparation: "Приготовление",
	document_breakdown: "Разбор сырья",
	wms_transfer_pos: "Перемещение",
	balance_element_avecos: "Пересчёт",
	documents_price: "Документ цен",
}

const SOURCE_ROUTES = {
	documents_in: "/documents-in/preview/",
	document_preparation: "/documents-preparation/preview/",
	document_breakdown: "/documents-breakdown/preview/",
	wms_transfer_pos: "/service/idocuments-in/preview/",
	documents_price: "/documents-price/preview/",
}

function rangeToToday(days) {
	const end = new Date()
	end.setHours(0, 0, 0, 0)
	const start = new Date(end)
	start.setDate(end.getDate() - (days - 1))
	return { start_date: localDateString(start), end_date: localDateString(end) }
}

// «2026-07-17 21:00:50.791521» → Date по местному времени
function toDate(value) {
	return new Date(String(value).slice(0, 19).replace(" ", "T"))
}

function formatDateTime(value) {
	if (!value) return "—"
	const date = toDate(value)
	const pad = (number) => String(number).padStart(2, "0")
	return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

// Тултип графиков в стиле карточек отчёта: заголовок и строки
function tooltipHtml(title, lines) {
	return `<div style="padding:10px 12px;min-width:180px;font-size:12px;line-height:1.6;">
			<div style="font-weight:700;margin-bottom:4px;color:var(--dark-text-strong, #111827);">${title}</div>
			${lines.map(line => `<div>${line}</div>`).join("")}
		</div>`
}

function tooltipDot(color) {
	return `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};margin-right:6px;"></span>`
}

// +42.9%, а целые без «.0» и с разрядами: +3 799 900%
function formatPercent(value) {
	const rounded = Math.round(Number(value || 0) * 10) / 10
	return `${rounded > 0 ? "+" : ""}${formatMoney(rounded, 1)}%`
}

// Рост себестоимости бьёт по марже — красный; рост цены продажи — зелёный
function changeColors(metric, value) {
	const number = Number(value || 0)
	if (number === 0) return { color: "#6b7280", background: "var(--dark-surface-2, #f1f4f8)" }
	const good = metric === "sale" ? number > 0 : number < 0
	return good
		? { color: "#1f9d57", background: "var(--dark-tint-success, #e7f8ef)" }
		: { color: "#d23434", background: "var(--dark-tint-danger, #fdecec)" }
}

function PercentBadge({ metric, value }) {
	if (value === null || value === undefined) return <span style={mutedStyle}>—</span>
	const number = Number(value)
	const icon = number > 0 ? "uil-arrow-up" : (number < 0 ? "uil-arrow-down" : "uil-minus")
	return (
		<span className="d-inline-flex align-items-center gap-1 text-nowrap" style={{ ...changeColors(metric, number), borderRadius: "14px", padding: "4px 9px", fontWeight: 800, fontSize: "12px" }}>
			<i className={`uil ${icon}`}></i>
			{formatPercent(number)}
		</span>
	)
}

function DeltaCell({ metric, value }) {
	const number = Number(value || 0)
	return <span className="text-nowrap" style={{ color: changeColors(metric, number).color, fontWeight: 800 }}>{number > 0 ? "+" : ""}{formatMoney(number)}</span>
}

function UnrecordedBadge() {
	return (
		<span
			className="text-nowrap"
			title="Цена в прейскуранте точки уже другая, а записи о смене в истории нет — когда поменяли, неизвестно"
			style={{ color: "#b26a00", background: "var(--dark-tint-warning, #fff4e0)", borderRadius: "14px", padding: "3px 8px", fontWeight: 700, fontSize: "11px" }}
		>
			без записи
		</span>
	)
}

function KpiCard({ title, value, sublabel, icon, color = SALE_COLOR }) {
	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body p-3">
				<div className="d-flex justify-content-between align-items-start">
					<div style={{ ...mutedStyle, textTransform: "uppercase", letterSpacing: "0.4px" }}>{title}</div>
					<div className="d-flex align-items-center justify-content-center" style={{ width: "30px", height: "30px", borderRadius: "10px", background: `${color}18`, color }}>
						<i className={`uil ${icon}`}></i>
					</div>
				</div>
				<h3 className="mb-0 mt-2" style={{ fontSize: "24px", fontWeight: 800, color: "var(--dark-text-strong, #111827)", letterSpacing: "-0.5px" }}>
					{value}
				</h3>
				{sublabel && <div style={{ ...mutedStyle, marginTop: "2px" }}>{sublabel}</div>}
			</div>
		</div>
	)
}

function IndexChart({ series, metric }) {
	const color = metric === "sale" ? SALE_COLOR : COST_COLOR
	const options = {
		chart: { type: "line", toolbar: { show: false }, zoom: { enabled: false }, locales: [apexRu], defaultLocale: "ru" },
		stroke: { curve: "straight", width: 3 },
		markers: { size: 4 },
		colors: [color],
		dataLabels: { enabled: false },
		grid: { borderColor: "var(--dark-border, #eef2f6)" },
		xaxis: { categories: series.map(point => point.label) },
		yaxis: { labels: { formatter: value => value === null || value === undefined ? "" : `${Number(value).toFixed(1)}` } },
		annotations: { yaxis: [{ y: 100, borderColor: "#9aa3af", strokeDashArray: 4, label: { text: "начало периода", style: { background: "transparent", color: "#9aa3af" } } }] },
		tooltip: {
			custom: ({ dataPointIndex }) => {
				const point = series[dataPointIndex]
				if (!point) return ""
				if (point.index === null || point.index === undefined) {
					return tooltipHtml(point.label, [`<span style="color:#8791a1;">нет данных за интервал</span>`])
				}
				const change = point.index - 100
				return tooltipHtml(point.label, [
					`${tooltipDot(color)}<b style="color:${changeColors(metric, change).color};">${formatPercent(change)}</b> к началу периода`,
					`<span style="color:#8791a1;">позиций в расчёте: ${formatMoney(point.items, 0)}</span>`,
				])
			},
		},
	}
	const chartSeries = [{ name: "Индекс корзины", data: series.map(point => point.index) }]

	return (
		<div className="card mb-3" style={cardStyle}>
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-1">
					<h5 className="mb-0">{metric === "sale" ? "Как менялись цены продажи" : "Как менялась себестоимость"}</h5>
					<span style={mutedStyle}>100 — уровень на начало периода; сумма значений всех позиций к их сумме на начало</span>
				</div>
				{series.length > 0
					? <ReactApexChart options={options} series={chartSeries} type="line" height={300} />
					: <div className="text-muted text-center py-5">Нет данных</div>
				}
			</div>
		</div>
	)
}

function TopChangesTable({ title, items, metric, showPos, emptyText, onOpen }) {
	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body">
				<h5 className="mb-3">{title}</h5>
				<div className="table-responsive">
					<table className="table table-sm align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0, fontSize: "13px" }}>
						<thead>
							<tr>
								<th className="py-1 text-muted fw-semibold" style={headerThStyle}>Товар</th>
								<th className="py-1 text-muted fw-semibold text-end" style={headerThStyle}>Было</th>
								<th className="py-1 text-muted fw-semibold text-end" style={headerThStyle}>Стало</th>
								<th className="py-1 text-muted fw-semibold text-end" style={headerThStyle}>Изменение</th>
							</tr>
						</thead>
						<tbody>
							{items.length === 0 && (
								<tr><td colSpan="4" className="text-muted text-center py-4">{emptyText}</td></tr>
							)}
							{items.map((item, index) => {
								const borderBottom = index === items.length - 1 ? "none" : "1px solid var(--dark-border, #eef2f6)"
								return (
									<tr key={`${item.pos_id}-${item.product_id}`} className="cursor-pointer" onClick={() => onOpen(item)}>
										<td className="py-1" style={{ borderBottom }}>
											<div style={{ fontWeight: 700 }}>{item.product_name}</div>
											{showPos && <div style={mutedStyle}>{item.pos_name}</div>}
										</td>
										<td className="py-1 text-end text-nowrap" style={{ borderBottom }}>{formatMoney(item.start_value)}</td>
										<td className="py-1 text-end text-nowrap" style={{ borderBottom }}>{formatMoney(item.end_value)}</td>
										<td className="py-1 text-end" style={{ borderBottom }}><PercentBadge metric={metric} value={item.delta_percent} /></td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

function ItemsTable({ items, metric, showPos, onOpen }) {
	const [visibleCount, setVisibleCount] = useState(50)
	const visible = items.slice(0, visibleCount)
	const remaining = items.length - visible.length

	return (
		<div className="card" style={cardStyle}>
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
					<div>
						<h5 className="mb-1">{metric === "sale" ? "Цены продажи по позициям" : "Себестоимость по позициям"}</h5>
						<div style={mutedStyle}>Клик по строке откроет график и все смены цены с документами</div>
					</div>
					<span style={{ ...mutedStyle, background: "var(--dark-surface-2, #f1f4f8)", borderRadius: "14px", padding: "4px 10px", color: "var(--dark-text-muted, #5b6472)" }}>
						{formatMoney(items.length, 0)} позиций
					</span>
				</div>
				<div className="table-responsive">
					<table className="table table-sm align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0, fontSize: "13px" }}>
						<thead>
							<tr>
								<th className="py-1 text-muted fw-semibold" style={headerThStyle}>Товар</th>
								{showPos && <th className="py-1 text-muted fw-semibold" style={headerThStyle}>Филиал</th>}
								<th className="py-1 text-muted fw-semibold text-end" style={headerThStyle}>На начало</th>
								<th className="py-1 text-muted fw-semibold text-end" style={headerThStyle}>На конец</th>
								<th className="py-1 text-muted fw-semibold text-end" style={headerThStyle}>Изменение</th>
								<th className="py-1 text-muted fw-semibold text-end" style={headerThStyle}>Δ %</th>
								<th className="py-1 text-muted fw-semibold text-end" style={headerThStyle}>Смен</th>
								<th className="py-1 text-muted fw-semibold text-end" style={headerThStyle}>Последняя смена</th>
							</tr>
						</thead>
						<tbody>
							{visible.length === 0 && (
								<tr><td colSpan={showPos ? 8 : 7} className="text-muted text-center py-4">Нет данных</td></tr>
							)}
							{visible.map((item, index) => {
								const borderBottom = index === visible.length - 1 ? "none" : "1px solid var(--dark-border, #eef2f6)"
								return (
									<tr key={`${item.pos_id}-${item.product_id}`} className="cursor-pointer" onClick={() => onOpen(item)}>
										<td className="py-1" style={{ borderBottom }}>
											<div style={{ fontWeight: 700 }}>{item.product_name}</div>
											<div style={mutedStyle}>{item.category_name || "без категории"}</div>
										</td>
										{showPos && <td className="py-1 text-muted" style={{ borderBottom }}>{item.pos_name}</td>}
										<td className="py-1 text-end text-nowrap" style={{ borderBottom }}>{formatMoney(item.start_value)}</td>
										<td className="py-1 text-end text-nowrap" style={{ borderBottom }}>{formatMoney(item.end_value)}</td>
										<td className="py-1 text-end" style={{ borderBottom }}><DeltaCell metric={metric} value={item.delta} /></td>
										<td className="py-1 text-end" style={{ borderBottom }}><PercentBadge metric={metric} value={item.delta_percent} /></td>
										<td className="py-1 text-end" style={{ borderBottom }}>{formatMoney(item.changes_count, 0)}</td>
										<td className="py-1 text-end text-muted text-nowrap" style={{ borderBottom }}>
											{item.unrecorded
												? <UnrecordedBadge />
												: (item.last_change_at ? formatDateTime(item.last_change_at) : "—")
											}
										</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
				{remaining > 0 && (
					<div className="text-center pt-3">
						<button className="btn btn-sm btn-link text-decoration-none" onClick={() => setVisibleCount(visibleCount + 50)}>
							Показать ещё ({remaining}) →
						</button>
					</div>
				)}
			</div>
		</div>
	)
}

// Ступенчатая линия: значение держится до следующей смены. Начало и конец периода
// добавляем точками, чтобы линия шла на всю ширину графика. В точке — её источник для тултипа.
function stepSeries(history, field, periodStart, periodEnd) {
	const prevField = field === "sale_price" ? "prev_sale_price" : "prev_cost_price"
	const data = []
	const startValue = history.start?.[field]
	if (startValue !== null && startValue !== undefined) {
		data.push({ x: periodStart, y: Number(startValue), note: "на начало периода" })
	}
	history.points.forEach(point => {
		if (point[field] === null || point[field] === undefined) return
		// Смена без записи — дата неизвестна, ставим на конец периода
		data.push({
			x: point.created_at ? toDate(point.created_at).getTime() : periodEnd,
			y: Number(point[field]),
			prev: point[prevField],
			note: point.unrecorded ? "смена без записи в истории" : (SOURCE_NAMES[point.entity_type] || "Документ"),
			unrecorded: point.unrecorded,
		})
	})
	const endValue = history.end?.[field]
	if (endValue !== null && endValue !== undefined && data.length > 0) {
		data.push({ x: periodEnd, y: Number(endValue), note: "на конец периода" })
	}
	return data
}

function formatTimestamp(timestamp) {
	const date = new Date(timestamp)
	const pad = (number) => String(number).padStart(2, "0")
	return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function ColorDot({ color }) {
	return <span style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "50%", background: color, marginRight: "6px" }}></span>
}

function ValueChange({ before, after }) {
	if (after === null || after === undefined) return <span style={mutedStyle}>—</span>
	const changed = before !== null && before !== undefined && Number(before) !== Number(after)
	if (!changed) return <span className="text-muted">{formatMoney(after)}</span>
	return (
		<span className="text-nowrap">
			<span style={mutedStyle}>{formatMoney(before)} → </span>
			<span style={{ fontWeight: 800 }}>{formatMoney(after)}</span>
		</span>
	)
}

function HistoryModal({ item, history, period, show, onHide }) {
	const periodStart = toDate(`${period.start_date} 00:00:00`).getTime()
	const periodEnd = Math.min(toDate(`${period.end_date} 23:59:59`).getTime(), Date.now())

	const chartSeries = history ? [
		{ name: "Цена продажи", data: stepSeries(history, "sale_price", periodStart, periodEnd) },
		{ name: "Себестоимость", data: stepSeries(history, "cost_price", periodStart, periodEnd) },
	] : []
	const chartOptions = {
		chart: { type: "line", toolbar: { show: false }, zoom: { enabled: false }, locales: [apexRu], defaultLocale: "ru" },
		stroke: { curve: "stepline", width: 3 },
		colors: [SALE_COLOR, COST_COLOR],
		markers: { size: 0, hover: { size: 5 } },
		dataLabels: { enabled: false },
		grid: { borderColor: "var(--dark-border, #eef2f6)" },
		// Подсказка с датой под осью перекрывала подписи месяцев — дата есть в тултипе
		xaxis: { type: "datetime", min: periodStart, max: periodEnd, labels: { datetimeUTC: false }, tooltip: { enabled: false } },
		yaxis: { labels: { formatter: value => formatMoney(value) } },
		// Легенда не нужна: цвета подписаны в шапке окна
		legend: { show: false },
		tooltip: {
			shared: false,
			intersect: false,
			custom: ({ seriesIndex, dataPointIndex, w }) => {
				const series = w.config.series[seriesIndex]
				const point = series?.data?.[dataPointIndex]
				if (!point) return ""
				const color = seriesIndex === 0 ? SALE_COLOR : COST_COLOR
				const changed = point.prev !== null && point.prev !== undefined && Number(point.prev) !== Number(point.y)
				const value = changed
					? `<span style="color:#8791a1;">${formatMoney(point.prev)} → </span><b>${formatMoney(point.y)}</b> сум`
					: `<b>${formatMoney(point.y)}</b> сум`
				return tooltipHtml(point.unrecorded ? "Дата неизвестна" : formatTimestamp(point.x), [
					`${tooltipDot(color)}${series.name}: ${value}`,
					`<span style="color:#8791a1;">${point.note}</span>`,
				])
			},
		},
	}

	function openDocument(point) {
		const route = SOURCE_ROUTES[point.entity_type]
		if (!route || !point.entity_id) return
		window.open(route + point.entity_id, "_blank", "noopener")
	}

	const points = history ? [...history.points].reverse() : []

	return (
		<Modal show={show} onHide={onHide} size="xl" centered scrollable>
			<Modal.Header closeButton>
				<Modal.Title>
					{item?.product_name || "История цены"}
					{item?.pos_name && <span style={{ ...mutedStyle, fontSize: "14px", marginLeft: "8px" }}>{item.pos_name}</span>}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ height: "75vh", overflowX: "hidden" }}>
				{!history && <div className="text-muted text-center py-5">Загружаем…</div>}
				{history && (
					<>
						<div className="row g-3 mb-2">
							<div className="col-md-6">
								<div style={mutedStyle}><ColorDot color={SALE_COLOR} />Цена продажи</div>
								<div style={{ fontWeight: 800, fontSize: "18px" }}>
									<ValueChange before={history.start.sale_price} after={history.end.sale_price} />
								</div>
							</div>
							<div className="col-md-6">
								<div style={mutedStyle}><ColorDot color={COST_COLOR} />Себестоимость</div>
								<div style={{ fontWeight: 800, fontSize: "18px" }}>
									<ValueChange before={history.start.cost_price} after={history.end.cost_price} />
								</div>
							</div>
						</div>
						<ReactApexChart options={chartOptions} series={chartSeries} type="line" height={300} />
						<div className="table-responsive mt-3">
							<table className="table table-sm table-bordered table-bordered-custom mb-0" style={{ fontSize: "13px" }}>
								<thead>
									<tr>
										<th>Дата</th>
										<th>Источник</th>
										<th className="text-end">Цена продажи</th>
										<th className="text-end">Себестоимость</th>
										<th>Кто</th>
									</tr>
								</thead>
								<tbody>
									{points.length === 0 && (
										<tr><td colSpan="5" className="text-center text-muted">За период цена не менялась</td></tr>
									)}
									{points.map((point, index) => {
										const hasDocument = SOURCE_ROUTES[point.entity_type] && point.entity_id
										return (
											<tr key={index} className={hasDocument ? "cursor-pointer" : ""} onClick={() => openDocument(point)}>
												<td className="text-nowrap">{point.unrecorded ? "дата неизвестна" : formatDateTime(point.created_at)}</td>
												<td>
													{point.unrecorded
														? <UnrecordedBadge />
														: <span className={hasDocument ? "text-primary" : ""}>{SOURCE_NAMES[point.entity_type] || "Документ"}</span>
													}
												</td>
												<td className="text-end"><ValueChange before={point.prev_sale_price} after={point.sale_price} /></td>
												<td className="text-end"><ValueChange before={point.prev_cost_price} after={point.cost_price} /></td>
												<td className="td-ellipsis">{point.created_by || "—"}</td>
											</tr>
										)
									})}
								</tbody>
							</table>
						</div>
					</>
				)}
			</Modal.Body>
		</Modal>
	)
}

function PriceDynamics() {
	const reduxAccount = useSelector(state => state.account)
	const [data, setData] = useState(null)
	const [historyItem, setHistoryItem] = useState(null)
	const [history, setHistory] = useState(null)

	const filters = useAnalyticsFilters({
		period: "custom",
		...rangeToToday(180),
		pos_id: reduxAccount.pos_id ?? -1,
		metric: "sale",
		product_type_ids: [1],
		menu_id: -1,
		category_id: -1,
		direction: "all",
		search: "",
	}, searchWithFilter)
	const { draft, applied, patch, patchApplied } = filters
	const options = useAnalyticsOptions(["menus", "categories"], draft.pos_id)

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl("/analytics/price-dynamics", filter), {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	// История одной пары «точка + товар» — лёгким запросом за тот же период
	async function openHistory(item) {
		setHistoryItem(item)
		setHistory(null)
		const url = buildAnalyticsUrl("/analytics/price-dynamics", {
			start_date: applied.start_date,
			end_date: applied.end_date,
		}, {
			history: 1,
			pos_id: item.pos_id,
			product_id: item.product_id,
		})
		const response = await GET(url, {}, { loader: true })
		if (httpOk(response)) setHistory(response.data)
	}

	function selectLongRange(days) {
		patch({ period: "custom", ...rangeToToday(days) })
	}

	const activeLongRange = longRangeOptions.find(option => {
		const range = rangeToToday(option.id)
		return draft.period === "custom" && draft.start_date === range.start_date && draft.end_date === range.end_date
	})

	const metric = data?.metric || applied.metric
	const summary = data?.summary || {}
	const showPos = Number(applied.pos_id) === -1
	const indexChange = Number(summary.index_change_percent || 0)

	return (
		<>
			<AnalyticsFilterBar
				title="Динамика цен"
				icon="uil-chart-line"
				subtitle="Как менялись цены продажи и себестоимость по точкам"
				filters={filters}
				presets={["30days", "this_month", "last_month", "custom"]}
				tabs={metricTabs}
				activeTab={applied.metric}
				onTabChange={(id) => patchApplied({ metric: id })}
			>
				<FilterField label="Длинный период">
					<FilterSegment options={longRangeOptions} value={activeLongRange?.id} onChange={selectLongRange} />
				</FilterField>
				<FilterField label="Филиал">
					<FilterSelect options={options.poses} allLabel="Все филиалы" value={draft.pos_id} onChange={(id) => patch({ pos_id: id })} />
				</FilterField>
				<FilterField label="Тип позиции">
					<FilterSelect isMulti options={productTypeOptions} allLabel="Все типы" value={draft.product_type_ids} onChange={(ids) => patch({ product_type_ids: ids })} />
				</FilterField>
				<FilterField label="Раздел меню">
					<FilterSelect options={options.menus} allLabel="Все разделы" value={draft.menu_id} onChange={(id) => patch({ menu_id: id })} />
				</FilterField>
				<FilterField label="Категория">
					<FilterSelect options={options.categories} allLabel="Все категории" value={draft.category_id} onChange={(id) => patch({ category_id: id })} />
				</FilterField>
				<FilterField label="Изменение">
					<FilterSelect options={directionOptions} value={draft.direction} onChange={(id) => patch({ direction: id })} />
				</FilterField>
				<FilterField label="Товар">
					<input className="form-control" style={inputStyle} value={draft.search} onChange={(event) => patch({ search: event.target.value })} placeholder="Поиск по названию" />
				</FilterField>
			</AnalyticsFilterBar>

			<div className="row g-3 mb-3">
				<div className="col-xl col-md-4 col-6">
					<KpiCard
						title="Уровень цен"
						value={formatPercent(indexChange)}
						sublabel="корзина на конец к началу периода"
						icon="uil-chart-line"
						color={changeColors(metric, indexChange).color}
					/>
				</div>
				<div className="col-xl col-md-4 col-6"><KpiCard title="Выросло" value={formatMoney(summary.increased, 0)} sublabel={`из ${formatMoney(summary.items, 0)} позиций`} icon="uil-arrow-up" color={changeColors(metric, 1).color} /></div>
				<div className="col-xl col-md-4 col-6"><KpiCard title="Снизилось" value={formatMoney(summary.decreased, 0)} icon="uil-arrow-down" color={changeColors(metric, -1).color} /></div>
				<div className="col-xl col-md-4 col-6"><KpiCard title="Без изменений" value={formatMoney(summary.unchanged, 0)} icon="uil-minus" color="#6b7280" /></div>
				<div className="col-xl col-md-4 col-6"><KpiCard title="Типичное изменение" value={formatPercent(summary.median_change_percent)} sublabel="медиана среди изменившихся" icon="uil-percentage" color={SALE_COLOR} /></div>
				<div className="col-xl col-md-4 col-6">
					<KpiCard
						title="Смен цены"
						value={formatMoney(summary.changes_count, 0)}
						sublabel={summary.unrecorded > 0 ? `из них ${formatMoney(summary.unrecorded, 0)} без записи в истории` : "за период"}
						icon="uil-history"
						color="#6b7280"
					/>
				</div>
			</div>

			<IndexChart series={data?.series || []} metric={metric} />

			<div className="row g-3 mb-3">
				<div className="col-lg-6">
					<TopChangesTable title="Топ-10 роста" items={data?.top_increases || []} metric={metric} showPos={showPos} emptyText="Ничего не выросло" onOpen={openHistory} />
				</div>
				<div className="col-lg-6">
					<TopChangesTable title="Топ-10 снижения" items={data?.top_decreases || []} metric={metric} showPos={showPos} emptyText="Ничего не снизилось" onOpen={openHistory} />
				</div>
			</div>

			<ItemsTable key={JSON.stringify(applied)} items={data?.items || []} metric={metric} showPos={showPos} onOpen={openHistory} />

			<HistoryModal
				item={historyItem}
				history={history}
				period={applied}
				show={!!historyItem}
				onHide={() => { setHistoryItem(null); setHistory(null) }}
			/>
		</>
	)
}

export default PriceDynamics
